import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { imgUrl } from "@/lib/api";
import SeoHead from "@/components/site/SeoHead";

/**
 * The Craft — a cinematic, image-rich atelier journey.
 * Dark charcoal ground, ivory type, warm crimson accents.
 * Six chapters, sticky top-nav, alternating editorial mosaic layouts,
 * scroll-triggered reveals, and macro captions under every plate.
 */

const CHAPTERS = [
  {
    id: "cotton",
    number: "01",
    kicker: "The fibre",
    title: "Long-staple cotton, grown slow.",
    lede: "We buy from three farms in Gujarat and Maharashtra. Long-staple, hand-picked, still humming with the field.",
    body:
      "Long-staple means longer fibres. Longer fibres mean fewer joins. Fewer joins mean a cloth that softens the more you love it — instead of pilling apart in three washes. It costs us more. We pay it because a child's shirt should outlast the child.",
    hero: {
      src: "/api/uploads/craft/cotton-field.webp",
      caption: "A quiet Gujarat field, hand-picked at first light.",
    },
    insets: [
      {
        src: "/api/uploads/craft/cotton-hands.webp",
        caption: "Fibre pulled from a boll — you can feel the weather in it.",
      },
      {
        src: "/api/uploads/craft/cotton-loom.webp",
        caption: "The warp on our wooden handloom.",
      },
    ],
  },
  {
    id: "dye",
    number: "02",
    kicker: "The colour",
    title: "The dye pot decides.",
    lede: "Natural pigments, small copper vats, twenty metres at a time. Some come out perfect. Some come out closer to something else.",
    body:
      "We work with a family of dyers in Kutch — third generation. Indigo from the leaf. Marigold from the flower. Madder from the root. Pomegranate skins, boiled twice, for the pink we can't reproduce any other way. Nothing is measured to the gram. Everything is measured by eye. We make peace with the difference.",
    hero: {
      src: "/api/uploads/craft/dye-pot-wide.webp",
      caption: "A copper vat, steam, and a length of cloth on a wooden pole.",
    },
    insets: [
      {
        src: "/api/uploads/craft/dye-swatches.webp",
        caption: "Four bowls, four plants. Indigo · madder · pomegranate · turmeric.",
      },
      {
        src: "/api/uploads/craft/dye-drying.webp",
        caption: "Drying on bamboo, waiting for the sun to finish the story.",
      },
    ],
  },
  {
    id: "embroidery",
    number: "03",
    kicker: "The hand",
    title: "Eleven hours for one butterfly.",
    lede: "We time our stitches. Not because we're in a hurry — because we want to know.",
    body:
      "Every motif on every piece is embroidered by hand, on a wooden hoop, in our workshop in Bengaluru. A butterfly takes eleven hours. A lotus, twenty. An ant, seven. A little red car — five, if the driver is in a good mood. Two women hold most of this world together: Kamla, and Sujatha.",
    hero: {
      src: "/api/uploads/craft/embroidery-hoop-hands.webp",
      caption: "Kamla and Sujatha, an embroidery hoop each, a Tuesday morning.",
    },
    insets: [
      {
        src: "/api/uploads/craft/embroidery-butterfly.webp",
        caption: "Magenta butterfly — one silk thread at a time.",
      },
      {
        src: "/api/uploads/craft/embroidery-ant-macro.webp",
        caption: "The ant with his leaf — small, deliberate, a little heroic.",
      },
    ],
  },
  {
    id: "buttons",
    number: "04",
    kicker: "The small things",
    title: "Coconut wood. Silk lining. Nothing metal.",
    lede: "The pieces most people don't notice — the pieces baby skin decides on.",
    body:
      "Buttons: coconut wood, sanded twice, sewn on with a double thread so they never come off in a wash. Lining: silk-touch cotton, side-seam labels only — never a scratchy tag at the neck. Thread: cotton, dyed the same day as the cloth. Every tiny choice is a stand.",
    hero: {
      src: "/api/uploads/craft/buttons-macro.webp",
      caption: "Six coconut wood buttons on hand-woven cotton.",
    },
    insets: [
      {
        src: "/api/uploads/craft/lining-silk.webp",
        caption: "Silk-touch lining — where the shirt meets the skin.",
      },
      {
        src: "/api/uploads/craft/trims-thread.webp",
        caption: "Cotton thread, dyed to match, spun on wooden spools.",
      },
    ],
  },
  {
    id: "stitch",
    number: "05",
    kicker: "The finish",
    title: "Hem, cuff, collar — by hand.",
    lede: "The edges are where the shortcuts hide. We refuse the shortcuts.",
    body:
      "Every hem is hand-turned. Every cuff hand-finished. Every collar sits on the neck the way we mean it to — because someone sat with it, adjusted it, and pressed it before it left the room. Then a hot iron. Then a soft fold. Then tissue.",
    hero: {
      src: "/api/uploads/craft/stitch-needle.webp",
      caption: "A single steel needle, mid-stitch, on ivory cotton.",
    },
    insets: [
      {
        src: "/api/uploads/craft/stitch-hem.webp",
        caption: "A hand-turned cuff on a child's shirt.",
      },
      {
        src: "/api/uploads/craft/iron-press.webp",
        caption: "The cast-iron press, still warm from Tuesday.",
      },
    ],
  },
  {
    id: "batches",
    number: "06",
    kicker: "Small runs",
    title: "Twenty pieces at a time.",
    lede: "We don't make thousands. We make enough. When a batch is finished, the batch is finished.",
    body:
      "Everything here — the dyeing, the embroidery, the stitching, the pressing, the wrapping — happens in the same room, on the same table, by the same six pairs of hands. Twenty pieces at a time. Once a batch sells through, we start the next one. There is no warehouse. There is no back stock. There is only what is on the table this month.",
    hero: {
      src: "/api/uploads/craft/batch-tissue.webp",
      caption: "Wrapping a small dress in cream tissue and jute twine.",
    },
    insets: [
      {
        src: "/api/uploads/craft/batch-fold.webp",
        caption: "The stack, folded, waiting for its tag.",
      },
    ],
  },
];

const useReveal = () => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setSeen(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, seen];
};

const Reveal = ({ children, delay = 0, className = "", testid }) => {
  const [ref, seen] = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${seen ? "in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
      data-testid={testid}
    >
      {children}
    </div>
  );
};

const TheCraft = () => {
  const [active, setActive] = useState("cotton");

  useEffect(() => {
    const handler = () => {
      const y = window.innerHeight * 0.4;
      let best = "cotton";
      let bestDist = Infinity;
      CHAPTERS.forEach((c) => {
        const el = document.getElementById(`chapter-${c.id}`);
        if (!el) return;
        const r = el.getBoundingClientRect();
        const dist = Math.abs(r.top - y);
        if (r.top < window.innerHeight && r.bottom > 0 && dist < bestDist) {
          bestDist = dist;
          best = c.id;
        }
      });
      setActive(best);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(`chapter-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="relative"
      style={{ background: "var(--ink)", color: "var(--ground)" }}
      data-testid="the-craft-page"
    >
      {/* ============ HERO ============ */}
      <section className="relative w-full" style={{ height: "100vh", minHeight: 640 }} data-testid="craft-hero">
        <img
          src={imgUrl("/api/uploads/craft/hero-atelier.webp")}
          alt="MELAVI atelier at dusk"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(28,26,25,0.55) 0%, rgba(28,26,25,0.35) 40%, rgba(28,26,25,0.85) 100%)",
          }}
        />
        <div className="grain absolute inset-0 opacity-45" />

        <div className="relative z-10 pt-32 md:pt-40 px-6 md:px-12 lg:px-24">
          <div className="max-w-[1440px] mx-auto flex items-center justify-between">
            <div className="nav-item" style={{ color: "var(--stone)" }}>The Craft · A dark chapter</div>
            <div className="hidden md:block nav-item" style={{ color: "var(--stone)" }}>Six rooms, one table</div>
          </div>
        </div>

        <div className="absolute inset-x-0 bottom-0 z-10">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 pb-20 md:pb-28">
            <div className="max-w-[900px]">
              <h1
                className="serif text-[52px] md:text-[92px] leading-[1.02]"
                style={{ color: "var(--ground)" }}
                data-testid="craft-hero-title"
              >
                The craft, <em className="italic">unhurried</em>.
              </h1>
              <p className="mt-8 max-w-[54ch] text-base md:text-lg leading-relaxed text-ground/80">
                Every MELAVI piece passes through six rooms and a small handful of hands.
                Scroll slowly. This is what happens between the field and the fold.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-6">
                <button
                  onClick={() => scrollTo("cotton")}
                  className="inline-flex items-center gap-3 nav-item text-ground/80 hover:text-ground transition-colors"
                  data-testid="craft-scroll-cta"
                >
                  <span>Begin the walk</span>
                  <span className="inline-block h-px w-14 bg-ground/60" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ STICKY CHAPTER NAV ============ */}
      <div
        className="sticky top-[76px] md:top-[88px] z-30 hairline-b"
        style={{
          background: "rgba(28,26,25,0.92)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          borderColor: "rgba(201,185,159,0.25)",
        }}
        data-testid="craft-sticky-nav"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-8 md:gap-14 overflow-x-auto no-scrollbar py-4">
            {CHAPTERS.map((c) => (
              <button
                key={c.id}
                onClick={() => scrollTo(c.id)}
                data-testid={`craft-nav-${c.id}`}
                className="flex-none group text-left"
              >
                <div
                  className="nav-item transition-colors"
                  style={{
                    color: active === c.id ? "var(--ground)" : "rgba(246,241,233,0.5)",
                  }}
                >
                  <span className="mr-2 text-[10px]" style={{ color: "#AE2B31" }}>{c.number}</span>
                  {c.kicker}
                </div>
                <div
                  className="mt-2 h-px transition-all"
                  style={{
                    width: active === c.id ? 40 : 12,
                    background: active === c.id ? "var(--ground)" : "rgba(246,241,233,0.25)",
                  }}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ============ CHAPTERS ============ */}
      {CHAPTERS.map((c, i) => {
        const flip = i % 2 === 1;
        const titleWords = c.title.split(" ");
        const tailCount = Math.min(2, titleWords.length);
        const head = titleWords.slice(0, titleWords.length - tailCount).join(" ");
        const tail = titleWords.slice(titleWords.length - tailCount).join(" ");
        return (
          <section
            key={c.id}
            id={`chapter-${c.id}`}
            className="relative py-24 md:py-36 hairline-b scroll-mt-40"
            style={{ borderColor: "rgba(201,185,159,0.18)" }}
            data-testid={`craft-chapter-${c.id}`}
          >
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
              {/* Chapter header row */}
              <div className="grid md:grid-cols-12 gap-8 mb-14 md:mb-20 items-end">
                <div className="md:col-span-1">
                  <Reveal>
                    <div
                      className="serif italic text-6xl md:text-7xl leading-none"
                      style={{ color: "rgba(174,43,49,0.85)" }}
                      data-testid={`craft-number-${c.id}`}
                    >
                      {c.number}
                    </div>
                  </Reveal>
                </div>
                <div className="md:col-span-6 md:col-start-2">
                  <Reveal delay={80}>
                    <div className="nav-item mb-3" style={{ color: "var(--stone)" }}>{c.kicker}</div>
                    <h2 className="serif text-4xl md:text-6xl leading-[1.04]" style={{ color: "var(--ground)" }}>
                      {head} <em className="italic">{tail}</em>
                    </h2>
                  </Reveal>
                </div>
                <div className="md:col-span-4 md:col-start-9">
                  <Reveal delay={160}>
                    <p className="text-base md:text-[17px] leading-relaxed text-ground/80">{c.lede}</p>
                  </Reveal>
                </div>
              </div>

              {/* Mosaic */}
              <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-stretch">
                <div className={`md:col-span-8 ${flip ? "md:order-2" : ""}`}>
                  <Reveal delay={100} testid={`craft-hero-plate-${c.id}`}>
                    <figure>
                      <div className="overflow-hidden bg-black/40">
                        <img
                          src={imgUrl(c.hero.src)}
                          alt={c.hero.caption}
                          className="w-full h-auto object-cover aspect-[16/10] transition-transform duration-[1600ms] ease-out hover:scale-[1.02]"
                          loading="lazy"
                        />
                      </div>
                      <figcaption
                        className="nav-item mt-4 text-[10px]"
                        style={{ color: "rgba(246,241,233,0.55)" }}
                      >
                        {c.hero.caption}
                      </figcaption>
                    </figure>
                  </Reveal>
                </div>

                <div className={`md:col-span-4 flex flex-col gap-6 md:gap-10 ${flip ? "md:order-1" : ""}`}>
                  {c.insets.map((ins, idx) => (
                    <Reveal key={idx} delay={200 + idx * 120} testid={`craft-inset-${c.id}-${idx}`}>
                      <figure>
                        <div className="overflow-hidden bg-black/40">
                          <img
                            src={imgUrl(ins.src)}
                            alt={ins.caption}
                            className="w-full h-auto object-cover aspect-[5/4] transition-transform duration-[1600ms] ease-out hover:scale-[1.02]"
                            loading="lazy"
                          />
                        </div>
                        <figcaption
                          className="nav-item mt-3 text-[10px]"
                          style={{ color: "rgba(246,241,233,0.55)" }}
                        >
                          {ins.caption}
                        </figcaption>
                      </figure>
                    </Reveal>
                  ))}
                </div>
              </div>

              {/* Body copy */}
              <div className="grid md:grid-cols-12 gap-8 mt-16 md:mt-24">
                <div className="md:col-span-6 md:col-start-4">
                  <Reveal delay={140}>
                    <p
                      className="serif italic text-[22px] md:text-[26px] leading-[1.5]"
                      style={{ color: "rgba(246,241,233,0.9)" }}
                      data-testid={`craft-body-${c.id}`}
                    >
                      {c.body}
                    </p>
                  </Reveal>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* ============ CLOSING QUOTE ============ */}
      <section className="relative py-28 md:py-40" data-testid="craft-outro">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <img
              src={imgUrl("/api/uploads/team/founders-sm.webp")}
              alt="Meghana TR and Lallitha Mukund"
              className="w-24 h-24 md:w-28 md:h-28 mx-auto object-cover rounded-full mb-8"
              style={{ border: "1px solid rgba(246,241,233,0.35)" }}
              data-testid="craft-founders-avatar"
            />
            <div className="serif italic text-2xl md:text-3xl leading-relaxed" style={{ color: "rgba(246,241,233,0.75)" }}>
              &ldquo;Nothing here is a machine. Not the dye, not the stitch,
              not the hand that folds the tissue at the end.&rdquo;
            </div>
            <div className="nav-item mt-8" style={{ color: "var(--stone)" }}>
              — Meghana &amp; Lallitha, founders
            </div>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-14 flex flex-wrap items-center justify-center gap-6">
              <Link
                to="/shop"
                className="btn-solid"
                data-testid="craft-shop-cta"
                style={{ background: "var(--ground)", color: "var(--ink)", borderColor: "var(--ground)" }}
              >
                Enter the showroom
              </Link>
              <Link to="/journal" className="nav-item" style={{ color: "var(--stone)" }} data-testid="craft-journal-cta">
                Read the journal →
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default TheCraft;
